import React, { useEffect, useMemo, useState } from 'react';
import { HiMagnifyingGlass, HiUser, HiUsers, HiXMark } from 'react-icons/hi2';
import DocumentBankPage from './DocumentBankPage';
import { listClientsBasic } from '../../services/documentBankService';
import { motion, AnimatePresence } from 'framer-motion';

const DEFAULT_SUGGESTIONS_LIMIT = 8;

const ClientDocumentBrowser = ({ title = 'Document Bank', description }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loadingResults, setLoadingResults] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Load default suggestions on mount
  useEffect(() => {
    let cancelled = false;
    const loadInitial = async () => {
      setLoadingResults(true);
      try {
        const results = await listClientsBasic({ max: DEFAULT_SUGGESTIONS_LIMIT });
        if (!cancelled) setSearchResults(results);
      } finally {
        if (!cancelled) setLoadingResults(false);
      }
    };
    loadInitial();
    return () => {
      cancelled = true;
    };
  }, []);

  // Debounced search when term changes
  useEffect(() => {
    const trimmed = searchTerm.trim();
    const handler = setTimeout(async () => {
      setHasSearched(!!trimmed);
      setLoadingResults(true);
      try {
        const results = await listClientsBasic({
          search: trimmed,
          max: DEFAULT_SUGGESTIONS_LIMIT,
        });
        setSearchResults(results);
      } finally {
        setLoadingResults(false);
      }
    }, 250);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  const activeResults = useMemo(() => {
    if (!searchResults?.length) return [];
    return searchResults.map(result => ({
      id: result.id,
      email: result.email || '',
      name: result.name || result.email || 'Unnamed client',
    }));
  }, [searchResults]);

  const handleSelectClient = client => {
    setSelectedClient(client);
    setSearchTerm('');
    setHasSearched(false);
  };

  const handleClearSelection = () => {
    setSelectedClient(null);
  };

  return (
    <div className="bg-gray-50">
      <DocumentBankPage
        key={selectedClient ? selectedClient.id : 'default'}
        forClientId={selectedClient?.id || null}
        forClientEmail={selectedClient?.email || null}
        allowStaffOnly
        allowGlobalScope
      />
    </div>
  );
};

export default ClientDocumentBrowser;
